import { DATE_YYYY_MM_DD } from '../constants/dateTimes';
import isSupportedVirtualTourUrl from '../helpers/listings/isSupportedVirtualTourUrl';
import Address, { AddressObject } from './Address';
import { OpenHouse, OpenHouseObject } from './OpenHouse';
import { RepliersListingStatus } from './repliers/RepliersListingStatus';
import { RepliersListingTransactionType } from './repliers/RepliersListingTransactionType';
import { instanceToPlain } from 'class-transformer';
import { FullListing } from './FullListing';
import { DateTime } from 'luxon';
import isEmpty from '../helpers/isEmpty';
import { RepliersListingLastStatus } from './repliers/RepliersListingLastStatus';
import colors from '../constants/colors';
import round from '../helpers/round';
import { RepliersListingBuildingClass } from './repliers/RepliersListingBuildingClass';
import isDefinedAndNonNull from '../utils/isDefinedAndNonNull';
import { UTC } from '../constants/timezones';
import tzlookup from 'tz-lookup';
import GpsCoordinates from './GpsCoordinates';
import { BOARD_RESOURCE_TO_NAME_MAP } from '../constants/realEstateBoards';
import { Place } from './Place';
import { PlaceType } from './PlaceType';

export type SimpleListingObject = {
  id?: string;
  mlsNumber: string;
  listDate: string;
  soldDate: null | string;
  listPrice: number;
  soldPrice: null | number;
  images: string[];
  blurhashes?: null | string[];
  details: {
    numBathrooms: null | number;
    numBathroomsPlus: null | number;
    numBedrooms: null | number;
    numBedroomsPlus: null | number;
    sqft: null | string;
    virtualTourUrl: null | string;
    numParkingSpaces: null | number;
    numGarageSpaces: null | number;
  };
  address: AddressObject;
  coordinates: GpsCoordinates;
  brokerageName: string;
  status: RepliersListingStatus;
  lastStatus: null | RepliersListingLastStatus;
  transactionType: RepliersListingTransactionType;
  buildingClass: RepliersListingBuildingClass;
  permissions: {
    canDisplayAddress: boolean;
    canDisplayPublic: boolean;
  };
  daysOnMarketFromMls: null | number;
  mlsUpdatedOn: string;
  resource: string;
  openHouses: null | Array<OpenHouseObject>;
  offerDate: null | string;
  offerCount: null | number;
  isFsbo: boolean;
  isPowerOfSale: null | boolean;
};

export class SimpleListing {
  id?: string;
  mlsNumber: string;
  listDate: DateTime;
  soldDate: null | DateTime;
  listPrice: number;
  soldPrice: null | number;
  images: string[];
  blurhashes: null | string[];
  details: {
    numBathrooms: null | number;
    numBathroomsPlus: null | number;
    numBedrooms: null | number;
    numBedroomsPlus: null | number;
    sqft: null | string;
    virtualTourUrl: null | string;
    numParkingSpaces: null | number;
    numGarageSpaces: null | number;
  };
  address: Address;
  coordinates: GpsCoordinates;
  brokerageName: string;
  status: RepliersListingStatus;
  lastStatus: null | RepliersListingLastStatus;
  transactionType: RepliersListingTransactionType;
  buildingClass: RepliersListingBuildingClass;
  permissions: {
    canDisplayAddress: boolean;
    canDisplayPublic: boolean;
  };
  daysOnMarketFromMls: null | number;
  mlsUpdatedOn: DateTime;
  resource: string;
  openHouses: null | Array<OpenHouse>;
  offerDate: null | DateTime;
  offerCount: null | number;
  isFsbo: boolean;
  isPowerOfSale: null | boolean;

  static fromObject(simpleListingObject: SimpleListingObject): SimpleListing {
    return new SimpleListing(simpleListingObject);
  }

  static fromFullListing(listing: FullListing): SimpleListing {
    return new SimpleListing({
      id: listing.id,
      mlsNumber: listing.mlsNumber,
      listDate: listing.listDate.toUTC().toString(),
      soldDate: listing.soldDate?.toUTC().toString() || null,
      listPrice: listing.listPrice,
      soldPrice: listing.soldPrice,
      images: listing.images,
      blurhashes: listing.blurhashes,
      details: {
        numBathrooms: listing.details.numBathrooms,
        numBathroomsPlus: listing.details.numBathroomsPlus,
        numBedrooms: listing.details.numBedrooms,
        numBedroomsPlus: listing.details.numBedroomsPlus,
        sqft: listing.details.sqft,
        virtualTourUrl: listing.details.virtualTourUrl,
        numParkingSpaces: listing.details.numParkingSpaces,
        numGarageSpaces: listing.details.numGarageSpaces,
      },
      address: listing.address.toObject(),
      coordinates: listing.coordinates,
      brokerageName: listing.brokerageName,
      status: listing.status,
      lastStatus: listing.lastStatus,
      transactionType: listing.transactionType,
      buildingClass: listing.buildingClass,
      permissions: listing.permissions,
      daysOnMarketFromMls: listing.daysOnMarketFromMls,
      mlsUpdatedOn: listing.mlsUpdatedOn.toUTC().toString(),
      resource: listing.resource,
      openHouses: listing.openHouses
        ? listing.openHouses.map(openHouse => openHouse.toObject())
        : null,
      offerDate: listing.offerDate?.toUTC().toString() || null,
      offerCount: listing.offerCount,
      isFsbo: listing.isFsbo,
      isPowerOfSale: listing.isPowerOfSale,
    });
  }

  constructor(simpleListingObject: SimpleListingObject) {
    if (simpleListingObject.id) {
      this.id = simpleListingObject.id;
    }
    this.mlsNumber = simpleListingObject.mlsNumber;
    this.listDate = DateTime.fromISO(simpleListingObject.listDate);
    this.soldDate = simpleListingObject.soldDate
      ? DateTime.fromISO(simpleListingObject.soldDate)
      : null;
    this.listPrice = simpleListingObject.listPrice;
    this.soldPrice = simpleListingObject.soldPrice;
    this.images = simpleListingObject.images;
    this.blurhashes = simpleListingObject.blurhashes || null;
    this.details = simpleListingObject.details;
    this.address = Address.fromObject(simpleListingObject.address);
    this.coordinates = simpleListingObject.coordinates;
    this.brokerageName = simpleListingObject.brokerageName;
    this.status = simpleListingObject.status;
    this.lastStatus = simpleListingObject.lastStatus;
    this.transactionType = simpleListingObject.transactionType;
    this.buildingClass = simpleListingObject.buildingClass;
    this.permissions = simpleListingObject.permissions;
    this.daysOnMarketFromMls = simpleListingObject.daysOnMarketFromMls;
    this.mlsUpdatedOn = DateTime.fromISO(simpleListingObject.mlsUpdatedOn);
    this.resource = simpleListingObject.resource;
    this.openHouses = simpleListingObject.openHouses
      ? simpleListingObject.openHouses.map(openHouseObject =>
          OpenHouse.fromObject(openHouseObject),
        )
      : null;
    this.offerDate = simpleListingObject.offerDate
      ? DateTime.fromISO(simpleListingObject.offerDate, { zone: UTC })
      : null;
    this.offerCount = simpleListingObject.offerCount;
    this.isFsbo = simpleListingObject.isFsbo;
    this.isPowerOfSale = simpleListingObject.isPowerOfSale;
  }

  soldDateText(): null | string {
    if (this.soldDate) {
      return this.soldDate.toFormat(DATE_YYYY_MM_DD);
    }
    return null;
  }

  bedroomsText(suffix = 'Bd'): null | string {
    if (isEmpty(this.details.numBedrooms)) return null;

    if (this.details.numBedrooms == 0 && this.details.numBedroomsPlus == 0)
      return `Studio`;

    let text = `${this.details.numBedrooms}`;
    if (Number(this.details.numBedroomsPlus) > 0) {
      text += `+${this.details.numBedroomsPlus}`;
    }
    text += ` ${suffix}`;

    return text;
  }

  bathroomsText(suffix = 'Ba'): null | string {
    if (isEmpty(this.details.numBathrooms)) return null;

    let text = `${this.details.numBathrooms}`;
    if (Number(this.details.numBathroomsPlus) > 0) {
      text += `+${this.details.numBathroomsPlus}`;
    }
    text += ` ${suffix}`;

    return text;
  }

  sqftText(): null | string {
    if (isEmpty(this.details.sqft) || this.details.sqft === '0') return null;

    return `${this.details.sqft} sqft`;
  }

  garageText(suffix = 'Ga'): null | string {
    if (isEmpty(this.details.numGarageSpaces)) return null;

    return `${this.details.numGarageSpaces} ${suffix}`;
  }

  spaceSummaryText(): string {
    const text = [
      this.bedroomsText(),
      this.bathroomsText(),
      this.garageText(),
      this.sqftText(),
    ];
    return text.filter(t => !!t).join(' | ');
  }

  latestPrice(): number {
    return this.soldPrice || this.listPrice;
  }

  lastStatusText(): string {
    switch (this.lastStatus) {
      case RepliersListingLastStatus.Sold:
        return 'Sold';
      case RepliersListingLastStatus.Leased:
        return 'Rented';
      case RepliersListingLastStatus.New:
        return this.transactionType === RepliersListingTransactionType.Sale
          ? 'For Sale'
          : 'For Rent';
      case RepliersListingLastStatus.PriceChanged:
        return 'Price Changed';
      case RepliersListingLastStatus.SoldConditionally:
        return 'Sold Conditionally';
      case RepliersListingLastStatus.LeasedConditionally:
        return 'Rented Conditionally';
      case RepliersListingLastStatus.Expired:
        return 'Expired';
      case RepliersListingLastStatus.Terminated:
        return 'Terminated';
      case RepliersListingLastStatus.Suspended:
        return 'Suspended';
      case RepliersListingLastStatus.Extended:
        return 'Extended';
      case RepliersListingLastStatus.DealFellThrough:
        return 'Deal Fell Through';
      case RepliersListingLastStatus.SoldConditionallyExtended:
        return 'Sold Conditionally Extended';
      default:
        return '';
    }
  }

  lastStatusColor(): string {
    const STATUS_TO_COLOR_MAPPING = {
      [RepliersListingLastStatus.New]: colors.forSale,
      [RepliersListingLastStatus.PriceChanged]: colors.forSale,
      [RepliersListingLastStatus.SoldConditionally]: colors.forSale,
      [RepliersListingLastStatus.LeasedConditionally]: colors.forSale,
      [RepliersListingLastStatus.Sold]: colors.soldOrLeased,
      [RepliersListingLastStatus.Leased]: colors.soldOrLeased,
      [RepliersListingLastStatus.Terminated]: colors.mediumGray,
      [RepliersListingLastStatus.Suspended]: colors.mediumGray,
      [RepliersListingLastStatus.Expired]: colors.mediumGray,
      [RepliersListingLastStatus.Extended]: colors.forSale,
      [RepliersListingLastStatus.DealFellThrough]: colors.forSale,
      [RepliersListingLastStatus.SoldConditionallyExtended]: colors.forSale,
    };

    return STATUS_TO_COLOR_MAPPING[this.lastStatus] || colors.mediumGray;
  }

  displayPriceWithStatus(): string {
    if (this.isSoldConditionally()) {
      return `${this.listPrice.formatAsPrice()} (${this.lastStatusText()})`;
    }
    if (this.isActive()) {
      return this.listPrice.formatAsPrice();
    }
    if (this.isSoldOrLeased()) {
      return `${this.soldPrice?.formatAsPrice()} (${this.lastStatusText()})`;
    }
    return `${this.listPrice.formatAsPrice()} (${this.lastStatusText()})`;
  }

  displayPrice(): string {
    if (this.isSoldOrLeased()) {
      if (!this.soldPrice || this.soldPrice === 0) {
        // This should happen only for commercial sold listings, which Repliers reports as having a price of $0
        return 'Undisclosed Price';
      } else {
        return this.soldPrice.formatAsPrice();
      }
    } else {
      return `${this.listPrice.formatAsPrice()}`;
    }
  }

  displayShortPrice(): string {
    if (this.isSoldOrLeased()) {
      if (!this.soldPrice || this.soldPrice === 0) {
        // This should happen only for commercial sold listings, which Repliers reports as having a price of $0
        return 'N/A';
      } else {
        return this.soldPrice.formatAsShortPrice();
      }
    } else {
      return `${this.listPrice.formatAsShortPrice()}`;
    }
  }

  isSoldWithBullyOffer() {
    // NOTE: we compare the UTC value of the sold date to the listing-timezone value of the offer date,
    // because the soldDate is always in UTC at the very start of the day, but the offerDate is an actual timestamp
    return (
      this.isSold() &&
      Boolean(this.soldDate) &&
      Boolean(this.offerDate) &&
      this.soldDate.toUTC().toFormat(DATE_YYYY_MM_DD) <
        this.offerDate.setZone(this.timezone()).toFormat(DATE_YYYY_MM_DD)
    );
  }

  isSold(): boolean {
    return this.lastStatus === RepliersListingLastStatus.Sold;
  }

  isLeased(): boolean {
    return this.lastStatus === RepliersListingLastStatus.Leased;
  }

  isSoldOrLeased(): boolean {
    return this.isSold() || this.isLeased();
  }

  isSoldConditionally(): boolean {
    return (
      this.lastStatus === RepliersListingLastStatus.SoldConditionally ||
      this.lastStatus === RepliersListingLastStatus.SoldConditionallyExtended
    );
  }

  isActive(): boolean {
    return this.status === RepliersListingStatus.Active;
  }

  daysOnMarket(): null | number {
    if (this.isActive()) {
      const daysOnMarket = this.listDate.diffNow('days').as('days') * -1;
      return round(daysOnMarket, 0);
    } else {
      return this.daysOnMarketFromMls;
    }
  }

  daysOnMarketText(): string {
    if (this.daysOnMarket() === null) {
      return '';
    }

    if (this.daysOnMarket() === 1) {
      return `${this.daysOnMarket()} day`;
    } else {
      return `${this.daysOnMarket()} days`;
    }
  }

  hasOpenHouseInFuture() {
    if (!this.hasOpenHouse()) return false;

    if (isDefinedAndNonNull(this.openHouses)) {
      for (const openHouse of this.openHouses) {
        if (openHouse.occursInFuture()) return true;
      }
    }

    return false;
  }

  hasOpenHouse(): boolean {
    return (
      Boolean(this.openHouses) &&
      Array.isArray(this.openHouses) &&
      this.openHouses.length > 0
    );
  }

  shouldDisplayOfferDate() {
    return (
      this.hasOfferDateYesterdayOrLater() &&
      [
        RepliersListingLastStatus.New,
        RepliersListingLastStatus.PriceChanged,
      ].includes(this.lastStatus)
    );
  }

  hasOfferDateYesterdayOrLater(): boolean {
    if (!this.offerDate) return false;

    return (
      DateTime.now()
        .setZone(this.timezone())
        .startOf('day')
        .minus({ days: 1 }) <
      this.offerDate.setZone(this.timezone()).endOf('day')
    );
  }

  offerDateText(): string | null {
    if (!this.offerDate) return null;

    return this.offerDate.setZone(this.timezone()).toFormat('EEE, MMM d, yyyy');
  }

  offerDateRelativeToTodayText(): string | null {
    if (!this.offerDate) return null;

    const timezonedOfferDateStartOfDay = this.offerDate
      .setZone(this.timezone())
      .startOf('day');
    const timezonedCurrentStartOfDay = DateTime.now()
      .setZone(this.timezone())
      .startOf('day');

    const MS_IN_DAY = 1000 * 60 * 60 * 24;
    const numOfDaysAway =
      (timezonedOfferDateStartOfDay.toMillis() -
        timezonedCurrentStartOfDay.toMillis()) /
      MS_IN_DAY;

    const roundedNumOfDaysAway = Math.round(numOfDaysAway); // NOTE: this is to deal with rare case of the time period straddling a daylight savings time change, which results in a 1-hours difference
    if (roundedNumOfDaysAway < -1)
      return `${Math.abs(roundedNumOfDaysAway)} days ago`;

    switch (roundedNumOfDaysAway) {
      case -1:
        return 'Yesterday';
      case 0:
        return 'Today';
      case 1:
        return 'Tomorrow';
      default:
        return `in ${roundedNumOfDaysAway} days`;
    }
  }

  hadAtLeastOneOffer() {
    return this.offerCount >= 1;
  }

  isLease(): boolean {
    return this.transactionType === RepliersListingTransactionType.Lease;
  }

  isSale(): boolean {
    return this.transactionType === RepliersListingTransactionType.Sale;
  }

  isCommercialBuilding(): boolean {
    return this.buildingClass === RepliersListingBuildingClass.Commercial;
  }

  isCondo(): boolean {
    return this.buildingClass === RepliersListingBuildingClass.Condo;
  }

  hasShowableVirtualTourUrl(): boolean {
    return (
      this.isActive() &&
      isDefinedAndNonNull(this.details.virtualTourUrl) &&
      isSupportedVirtualTourUrl(this.details.virtualTourUrl)
    );
  }

  previewImageUrl(): string {
    return `https://api.tophouse.com/api/v1/listings/${this.id}/previewImage`;
  }

  timezone(): string {
    return tzlookup(this.coordinates.latitude, this.coordinates.longitude);
  }

  realEstateBoardName(): string {
    return (
      BOARD_RESOURCE_TO_NAME_MAP[this.resource] || 'Unknown Real Estate Board'
    );
  }

  toObject(): SimpleListingObject {
    return {
      ...instanceToPlain(this),
      listDate: this.listDate.toUTC().toString(),
      soldDate: this.soldDate?.toUTC().toString(),
      mlsUpdatedOn: this.mlsUpdatedOn.toUTC().toString(),
      openHouses: this.openHouses
        ? this.openHouses.map(openHouse => openHouse.toObject())
        : null,
      offerDate: this.offerDate?.toUTC().toString(),
    } as SimpleListingObject;
  }

  toPlace(): Place {
    const address = this.address;

    return Place.fromObject({
      type: PlaceType.Address,
      name: `${address.toString({
        city: true,
        abbreviatedState: true,
      })}, ${address.zip}`, // Note: the zip/postal code needs to be here because we rely on it sometimes for reverse lookups
      center: [this.coordinates.longitude, this.coordinates.latitude],
    });
  }
}
